import { useEffect, useRef, useState } from "react";
import { ytThumb, ytThumbFallback, resolveUrl } from "@/lib/api";
import { Play } from "lucide-react";

export const isVertical = (p) => (p.format || "").includes("9:16");

export const cardImage = (p) => {
  if (p.custom_thumbnail) return resolveUrl(p.custom_thumbnail);
  if (p.youtube_id) return ytThumb(p.youtube_id);
  return null;
};

// Self-contained reveal driven by React state so it never conflicts with
// re-renders (filtering) the way an imperatively-added class would.
const useRevealOnScroll = (visible) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); io.disconnect(); } },
      { threshold: 0.12 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // If a card is ever shown after being hidden by a filter, guarantee it is
  // revealed (skip initial mount to preserve the scroll-in animation).
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; return; }
    if (visible) setInView(true);
  }, [visible]);

  return { ref, inView };
};

const CardMedia = ({ project, vertical, hasVideo, onOpen }) => {
  const img = cardImage(project);

  const handleImgError = (e) => {
    if (project.youtube_id && !e.target.dataset.fallback) {
      e.target.dataset.fallback = "1";
      e.target.src = ytThumbFallback(project.youtube_id);
    } else {
      e.target.style.display = "none";
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Play ${project.title}`}
      onClick={() => hasVideo && onOpen(project)}
      onKeyDown={(e) => { if (hasVideo && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onOpen(project); } }}
      className={`relative overflow-hidden rounded-xl bg-surface border border-hairline ${hasVideo ? "cursor-pointer" : ""} ${vertical ? "aspect-[9/16]" : "aspect-video"}`}
    >
      {img ? (
        <img
          src={img}
          alt={project.title}
          loading="lazy"
          onError={handleImgError}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
        />
      ) : (
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg,#1b1b24,#13131a 60%,#0d0d12)" }} />
      )}

      {hasVideo && (
        <span className="absolute top-3 right-3 z-20 flex items-center gap-1.5 rounded-full bg-black/70 backdrop-blur px-2.5 py-1 font-mono text-[11px] text-txt">
          <Play size={11} className="fill-lav text-lav" /> watch with sound
        </span>
      )}

      <div className="prompt-drawer absolute inset-x-0 bottom-0 z-10 p-4 sm:p-5 bg-black/55 backdrop-blur-md border-t border-lav/40">
        <p className="font-mono text-[11px] sm:text-xs leading-relaxed text-txt/90">
          <span className="text-lav">&gt; </span>{project.prompt}
        </p>
      </div>
    </div>
  );
};

const CardInfo = ({ project }) => (
  <>
    <div className="mt-3 flex items-center justify-between font-mono text-[10px] sm:text-[11px] uppercase tracking-wide">
      <span className="text-lav truncate pr-2">{project.model}</span>
      <span className="text-mute whitespace-nowrap">{project.format}</span>
    </div>

    <div className="mt-1.5 flex items-start justify-between gap-3">
      <div className="min-w-0">
        <h3 className="font-display text-lg sm:text-xl text-txt leading-snug line-clamp-1" data-testid="project-title">{project.title}</h3>
        <p className="font-body text-sm text-mute mt-0.5 line-clamp-2 min-h-[2.5rem]">{project.descriptor}</p>
      </div>
      <span className="shrink-0 mt-1 font-mono text-[10px] uppercase tracking-wide text-mute border border-hairline rounded-full px-2 py-1">
        {project.categoryLabel}
      </span>
    </div>
  </>
);

const ProjectCard = ({ project, index, onOpen, visible = true }) => {
  const vertical = isVertical(project);
  const hasVideo = !!project.youtube_id || !!project.custom_video;
  const { ref, inView } = useRevealOnScroll(visible);

  return (
    <article
      ref={ref}
      className={`group flex-col ${visible ? "flex" : "hidden"}`}
      style={{
        transitionDelay: `${(index % 3) * 70}ms`,
        opacity: inView ? 1 : 0,
        transform: inView ? "none" : "translateY(28px)",
        transition: "opacity .9s var(--ease), transform .9s var(--ease)",
      }}
      data-testid="project-card"
      data-category={project.category}
      data-visible={visible ? "true" : "false"}
    >
      <CardMedia project={project} vertical={vertical} hasVideo={hasVideo} onOpen={onOpen} />
      <CardInfo project={project} />
    </article>
  );
};

export default ProjectCard;
